/** Implementation borrowed from https://github.com/joakimbeng/kebab-case */
const KEBAB_REGEX = /[A-Z\u00C0-\u00D6\u00D8-\u00DE]/g;
function kebabCase(str) {
    return str.replace(KEBAB_REGEX, match => '-' + match.toLowerCase());
}

/**
 * Gets the data attributes on an `HTMLElement`.
 *
 * @example
```keepDataPrefix === false```:
```html
<div data-service="Foo" data-service-type="bar"></div> -> { 'service': 'Foo', 'service-type': 'Bar' }
```

```keepDataPrefix === true```:
```html
<div data-service="Foo" data-service-type="bar"></div> -> { 'data-service': 'Foo', 'data-service-type': 'Bar' }
```
 */
function getDataAttributes(element, keepDataPrefix = false) {
    return Object.entries(element.dataset).reduce((acc, [key, value]) => (Object.assign(Object.assign({}, acc), { [calculateKey(key, keepDataPrefix)]: value })), {});
}
function calculateKey(key, keepDataPrefix) {
    return `${keepDataPrefix && 'data-'}${kebabCase(key)}`;
}

/**
 * Noop function - doesn't do anything
 */
const noop = (...args) => {
    args.forEach(Object.keys);
};

// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
/**
 * Omit a a set of properties from an object.
 *
 * Does **not** modify the original object.
 * @param obj the object
 * @param keys the keys
 * @returns A new object with all properties apart from the `keys` from `obj`
 */
function omit(obj, ...keys) {
    return Object.keys(obj)
        .filter(key => !keys.includes(key))
        .reduce((acc, key) => Object.assign(acc, { [key]: obj[key] }), {});
}

// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
/**
 * Pick a a set of properties from an object.
 *
 * Does **not** modify the original object.
 * @param obj the object
 * @param keys the keys
 * @returns A new object with all properties from the `keys` from `obj`
 */
function pick(obj, ...keys) {
    return keys.reduce((acc, key) => Object.assign(acc, { [key]: obj[key] }), {});
}

const proxyHandlerMap = new Map();
function Styled(componentName) {
    return function (constructor) {
        const ngOnInit = constructor.prototype.ngOnInit;
        constructor.prototype.ngOnInit = function () {
            const handler = proxyHandlerMap.get(componentName);
            if (handler && handler.beforeNgInit) {
                handler.beforeNgInit.apply(this);
            }
            if (ngOnInit) {
                ngOnInit.apply(this);
            }
        };
        const ngOnChanges = constructor.prototype.ngOnChanges;
        constructor.prototype.ngOnChanges = function (changes) {
            const handler = proxyHandlerMap.get(componentName);
            if (handler && handler.beforeNgChanges) {
                handler.beforeNgChanges.apply(this, [changes]);
            }
            if (ngOnChanges) {
                ngOnChanges.apply(this, [changes]);
            }
        };
        const ngOnDestroy = constructor.prototype.ngOnDestroy;
        constructor.prototype.ngOnDestroy = function () {
            const handler = proxyHandlerMap.get(componentName);
            if (handler && handler.beforeNgDestroy) {
                handler.beforeNgDestroy.apply(this);
            }
            if (ngOnDestroy) {
                ngOnDestroy.apply(this);
            }
        };
        const ngDoCheck = constructor.prototype.ngDoCheck;
        constructor.prototype.ngDoCheck = function () {
            const handler = proxyHandlerMap.get(componentName);
            if (handler && handler.beforeNgDoCheck) {
                handler.beforeNgDoCheck.apply(this);
            }
            if (ngDoCheck) {
                ngDoCheck.apply(this);
            }
        };
        const ngAfterContentInit = constructor.prototype.ngAfterContentInit;
        constructor.prototype.ngAfterContentInit = function () {
            const handler = proxyHandlerMap.get(componentName);
            if (handler && handler.beforeNgAfterContentInit) {
                handler.beforeNgAfterContentInit.apply(this);
            }
            if (ngAfterContentInit) {
                ngAfterContentInit.apply(this);
            }
        };
        const ngAfterContentChecked = constructor.prototype.ngAfterContentChecked;
        constructor.prototype.ngAfterContentChecked = function () {
            const handler = proxyHandlerMap.get(componentName);
            if (handler && handler.beforeNgAfterContentChecked) {
                handler.beforeNgAfterContentChecked.apply(this);
            }
            if (ngAfterContentChecked) {
                ngAfterContentChecked.apply(this);
            }
        };
        const ngAfterViewInit = constructor.prototype.ngAfterViewInit;
        constructor.prototype.ngAfterViewInit = function () {
            const handler = proxyHandlerMap.get(componentName);
            if (handler && handler.beforeNgAfterViewInit) {
                handler.beforeNgAfterViewInit.apply(this);
            }
            if (ngAfterViewInit) {
                ngAfterViewInit.apply(this);
            }
        };
        const ngAfterViewChecked = constructor.prototype.ngAfterViewChecked;
        constructor.prototype.ngAfterViewChecked = function () {
            const handler = proxyHandlerMap.get(componentName);
            if (handler && handler.beforeNgAfterViewChecked) {
                handler.beforeNgAfterViewChecked.apply(this);
            }
            if (ngAfterViewChecked) {
                ngAfterViewChecked.apply(this);
            }
        };
        return constructor;
    };
}
function proxy(fabComponentName, handlers) {
    proxyHandlerMap.set(fabComponentName, handlers);
}

/**
 * Generated bundle index. Do not edit.
 */

export { Styled, getDataAttributes, kebabCase, noop, omit, pick, proxy };
